import { useEffect, useRef, type RefObject } from "react";
import {
  ArrowDown,
  ArrowRight,
  Headphones,
  Users,
  Zap,
  Lock,
  Mail,
  Clock,
  Smartphone,
} from "lucide-react";

function useFadeIn(): RefObject<HTMLDivElement | null> {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) el.classList.add("visible");
      },
      { threshold: 0.12 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return ref;
}

function Hero() {
  return (
    <section className="hero">
      <div className="hero-glow" />
      <div className="hero-rings">
        <div className="ring ring-1" />
        <div className="ring ring-2" />
        <div className="ring ring-3" />
      </div>

      <img
        src="/logo.png"
        alt="Crosstlk"
        className="hero-logo"
        width={88}
        height={88}
      />

      <span className="badge">
        <Clock size={13} />
        Coming Soon
      </span>

      <h1>
        Your music.
        <br />
        <span className="accent">Their app.</span>
        <br />
        No problem.
      </h1>

      <p className="hero-sub">
        You use Spotify. Your friend uses Apple&nbsp;Music.
        <br />
        Send them a song&nbsp;&mdash; it opens right in their app.
      </p>

      <a
        className="btn btn-primary"
        href="mailto:hello@crosstlk.xyz?subject=Early%20Access&body=I'd%20like%20early%20access%20to%20Crosstlk!"
      >
        <Mail size={16} />
        Get Early Access
      </a>

      <div className="scroll-cue">
        <ArrowDown size={16} />
      </div>
    </section>
  );
}

function Problem() {
  const ref = useFadeIn();
  return (
    <section className="problem fade-in" ref={ref}>
      <div className="container">
        <p className="quote">
          &ldquo;Can you send that song again? I don&rsquo;t have Spotify.&rdquo;
        </p>
        <p className="answer">
          Sound familiar? Every music app lives in its own world.
          <br />
          Crosstlk connects them all.
        </p>
      </div>
    </section>
  );
}

function HowItWorks() {
  const ref = useFadeIn();
  return (
    <section className="how" id="how">
      <div className="container fade-in" ref={ref}>
        <h2>Share a song in seconds</h2>
        <p className="how-sub">
          It doesn&rsquo;t matter what app you or your friends use.
        </p>

        <div className="flow">
          <div className="flow-step">
            <div className="flow-icon">
              <Headphones size={22} />
            </div>
            <h3>Pick a song</h3>
            <p>
              Drop in a link from any music app, or choose from what you&rsquo;ve
              been listening to.
            </p>
          </div>

          <div className="flow-arrow">
            <ArrowRight size={20} />
          </div>

          <div className="flow-step">
            <div className="flow-icon">
              <Zap size={22} />
            </div>
            <h3>We find it everywhere</h3>
            <p>
              Crosstlk instantly matches the song across Spotify, Apple Music,
              Tidal, YouTube Music, Deezer, and more.
            </p>
          </div>

          <div className="flow-arrow">
            <ArrowRight size={20} />
          </div>

          <div className="flow-step">
            <div className="flow-icon">
              <Smartphone size={22} />
            </div>
            <h3>They tap, they hear it</h3>
            <p>
              Your friend opens the link and the song plays in whatever app they
              already use. Done.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

const HIGHLIGHTS = [
  {
    icon: Users,
    title: "Find friends nearby",
    desc: "Walk into a room and see who\u2019s around. No adding usernames, no scanning codes.",
  },
  {
    icon: Headphones,
    title: "Your own music feed",
    desc: "Every song your friends send you, in one place. No more losing that track someone sent you last week.",
  },
  {
    icon: Lock,
    title: "Private by default",
    desc: "We don\u2019t track what you listen to. No ads, no data selling. Just you and your friends sharing music.",
  },
];

function Highlights() {
  const ref = useFadeIn();
  return (
    <section className="highlights">
      <div className="container fade-in" ref={ref}>
        <h2>More than a link converter</h2>
        <div className="highlight-grid">
          {HIGHLIGHTS.map((h) => (
            <div className="highlight-card" key={h.title}>
              <div className="highlight-icon">
                <h.icon size={22} />
              </div>
              <h3>{h.title}</h3>
              <p>{h.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const SERVICES = [
  { name: "Spotify", color: "#1DB954" },
  { name: "Apple Music", color: "#FC3C44" },
  { name: "YouTube Music", color: "#FF0000" },
  { name: "Tidal", color: "#FFFFFF" },
  { name: "Deezer", color: "#A238FF" },
  { name: "SoundCloud", color: "#FF5500" },
  { name: "Amazon Music", color: "#00A8E1" },
];

function Platforms() {
  const ref = useFadeIn();
  return (
    <section className="platforms fade-in" ref={ref}>
      <p className="platforms-label">Works with</p>
      <div className="platform-row">
        {SERVICES.map((s) => (
          <span className="platform-chip" key={s.name}>
            <span className="dot" style={{ background: s.color }} />
            {s.name}
          </span>
        ))}
      </div>
    </section>
  );
}

function CtaSection() {
  const ref = useFadeIn();
  return (
    <section className="cta-section fade-in" ref={ref}>
      <div className="cta-glow" />
      <h2>
        One song. Every app.
        <br />
        Zero hassle.
      </h2>
      <p className="cta-sub">
        Free on iPhone and Android. We&rsquo;ll let you know the moment
        it&rsquo;s ready.
      </p>
      <a
        className="btn btn-primary btn-lg"
        href="mailto:hello@crosstlk.xyz?subject=Early%20Access&body=I'd%20like%20early%20access%20to%20Crosstlk!"
      >
        <Mail size={18} />
        Get Early Access
      </a>
    </section>
  );
}

function Footer() {
  return (
    <footer>
      <div className="footer-links">
        <a href="/privacy">Privacy</a>
        <a href="mailto:hello@crosstlk.xyz">Contact</a>
      </div>
      <p className="footer-copy">
        &copy; {new Date().getFullYear()} Crosstlk
      </p>
    </footer>
  );
}

export function App() {
  return (
    <>
      <Hero />
      <Problem />
      <HowItWorks />
      <Highlights />
      <Platforms />
      <CtaSection />
      <Footer />
    </>
  );
}
